import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Select } from "antd";
import { useTranslation } from "react-i18next";

/**
 * ShippingTypeSelect - 共用物流類型選擇元件
 *
 * 根據 merchantId 查詢可用的物流類型，支援 COD 過濾和 platform 物流保留。
 *
 * @param {string} merchantId - 貨主 ID，篩選已啟用的物流類型
 * @param {boolean} [codOnly=false] - 只顯示支援 COD 的物流
 * @param {string} [currentShippingTypeId] - 當前物流 ID，確保永遠出現（即使是 platform 類型）
 * @param {string} [value] - 選中的 shipping_type_id
 * @param {Function} [onChange] - 選擇回調
 * @param {boolean} [disabled=false] - 禁用
 * @param {string} [role='operator'] - 'operator' | 'admin'，決定 API 來源
 */
const ShippingTypeSelect = ({
  merchantId,
  codOnly = false,
  currentShippingTypeId,
  value,
  onChange,
  disabled = false,
  role = "operator",
  ...rest
}) => {
  const { t } = useTranslation();
  const [shippingTypes, setShippingTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchShippingTypes = useCallback(async () => {
    if (!merchantId) return;
    setLoading(true);
    try {
      let service;
      if (role === "admin") {
        const mod = await import("@services/admin/shippingTypeService");
        service = mod.shippingTypeService;
      } else {
        const mod =
          await import("@services/operator/merchantShippingTypesService");
        service = mod.merchantShippingTypesService;
      }

      const result =
        role === "admin"
          ? await service.index({ merchant_id: merchantId })
          : await service.index(merchantId);

      if (result.success && result.data) {
        setShippingTypes(result.data);
      }
    } catch (error) {
      console.error("Failed to fetch shipping types:", error);
    } finally {
      setLoading(false);
    }
  }, [merchantId, role]);

  useEffect(() => {
    fetchShippingTypes();
  }, [fetchShippingTypes]);

  // 三種可能的 API 回應 shape：
  //  - admin shipping_types#index：扁平 `{ id, code, name, source }`
  //  - operator merchant_shipping_types#index：扁平加前綴 `{ shipping_type_id, shipping_type_code, shipping_type_name, shipping_type_source }`
  //  - 內嵌 `{ shipping_type: { id, code, name, source } }`
  const resolveShippingType = (st) => ({
    id: st.shipping_type?.id || st.shipping_type_id || st.id,
    code: st.shipping_type?.code || st.shipping_type_code || st.code,
    name: st.shipping_type?.name || st.shipping_type_name || st.name,
    source: st.shipping_type?.source || st.shipping_type_source || st.source,
    supportsCod: st.shipping_type?.supports_cod ?? st.supports_cod,
  });

  const options = useMemo(() => {
    let filtered = shippingTypes.filter((st) => {
      const { id, source } = resolveShippingType(st);
      // 排除 platform 物流，除非是當前使用的物流
      if (source === "platform") {
        return id === currentShippingTypeId;
      }
      return true;
    });

    // COD 過濾
    if (codOnly) {
      filtered = filtered.filter((st) => resolveShippingType(st).supportsCod);
    }

    // 分組：系統 vs 自訂
    const systemTypes = [];
    const customTypes = [];

    filtered.forEach((st) => {
      const { id, code, name, source } = resolveShippingType(st);
      const label = t(`shipping_types.${code}`, { defaultValue: name });

      const option = { value: id, label };

      if (source === "custom") {
        customTypes.push(option);
      } else {
        systemTypes.push(option);
      }
    });

    // 排序
    const sortByLabel = (a, b) => a.label.localeCompare(b.label);
    systemTypes.sort(sortByLabel);
    customTypes.sort(sortByLabel);

    return { systemTypes, customTypes };
  }, [shippingTypes, codOnly, currentShippingTypeId, t]);

  const allOptions = useMemo(() => {
    const groups = [];
    if (options.systemTypes.length > 0) {
      groups.push({
        label: t("shipping_types.groups.system", { defaultValue: "系統物流" }),
        options: options.systemTypes,
      });
    }
    if (options.customTypes.length > 0) {
      groups.push({
        label: t("shipping_types.groups.custom", { defaultValue: "自訂物流" }),
        options: options.customTypes,
      });
    }
    return groups;
  }, [options, t]);

  return (
    <Select
      showSearch
      value={value}
      onChange={onChange}
      disabled={disabled}
      loading={loading}
      options={allOptions}
      filterOption={(input, option) =>
        option.label?.toUpperCase().includes(input.toUpperCase())
      }
      placeholder={t("common.select.please_select")}
      {...rest}
    />
  );
};

export default ShippingTypeSelect;
